import { useState } from 'react'
import { Typography, Box, useMediaQuery } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import IconDropdown from 'components/IconDropdown/IconDropdown'
import SecondarySidebar from 'components/SecondarySidebar/SecondarySidebar'
import { useAlert } from 'providers/AlertProvider'
import theme from 'lib/theme'
import { SidebarLinkWrapper } from 'views/Order/Order.styled'
import { usePatchOrderStatus } from 'hooks/useOrders'
import { Order, OrderPart, ORDER_STATUS } from 'types/api/order'
import { useSideBarUiContext } from 'components/Layout/LayoutWithSecondarySidebar'
import { OrderCancellationModal } from './OrderCancellationModal'

type OrderSecondarySidebarProps = {
  orderId: string
  storeName: string
  order: Order
}

const navLinks = {
  FULFILMENT: 'fulfilment',
  INFO: 'info',
  PRODUCTS: 'products',
  EVENTS: 'events',
}

export const OrderSecondarySidebar = ({
  orderId,
  order,
  storeName,
}: OrderSecondarySidebarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const prevListingPage = localStorage.getItem('ordersListingPage')
  const [modal, setModal] = useState({
    right: false,
  })

  const { setShowOverlay } = useSideBarUiContext()
  const { setAlert } = useAlert()
  const { mutateAsync } = usePatchOrderStatus(orderId as string)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChangeOrderStatus = async (
    part: OrderPart,
    newStatus: string
  ) => {
    const values = {
      status: newStatus,
      crc: part.crc,
      details: {},
    }
    await mutateAsync([values, part.id], {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const handleOpenModal = () => {
    setModal({ right: true })
  }
  const handleCloseModal = () => {
    setModal({ right: false })
  }

  const getDropdownOption = (status: string) => {
    if (status === 'collected') {
      return [{ label: t('orderProduct.revertStatusToOpen'), value: 'open' }]
    }
    if (status === 'delivering') {
      return [
        {
          label: t('orderProduct.revertStatusToCollected'),
          value: 'collected',
        },
        {
          label: t('orderProduct.moveToDelivered'),
          value: 'delivered',
        },
      ]
    }
    return [{ label: '', value: '' }]
  }

  return (
    <>
      <SecondarySidebar
        header={t(`orderInfo.orderNumber`, {
          visibleId: order.visibleId,
        })}
        subtitle={t('orderInfo.parentId', { parentId: order.id })}
        subtitle2={storeName}
        selectOptionsBtn={
          <IconDropdown
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth='160px'
            Icon={MoreHorizIcon}
            isIconOnly
            disabled={
              order.parts[0].status === ORDER_STATUS.CANCELLED ||
              order.parts.some((part) => part.status === ORDER_STATUS.DELIVERED)
            }
            options={[
              {
                value: 'cancelOrder',
                label: t('orderProduct.cancelOrder'),
              },
            ]}
            ariaLabel={t('orderProduct.cancelOrder-aria')}
            onChange={handleOpenModal}
          />
        }
        onGoBackClick={() =>
          navigate(`/order${prevListingPage}`, {
            state: { placeName: storeName },
          })
        }>
        <SidebarLinkWrapper
          highlighted={location.pathname.split('/')[3] === navLinks.INFO}
          display='flex'
          alignItems='center'>
          <Link
            onClick={() => isMobile && setShowOverlay(true)}
            style={{
              textDecoration: 'none',
              color:
                location.pathname.split('/')[3] === navLinks.INFO
                  ? theme.palette.primary.main
                  : theme.palette.commonColors.black,
              width: '223px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
            }}
            to={`/order/${orderId}/info`}>
            <Typography variant='body2' ml={1}>
              {t('orderInfo.orderInformation')}
            </Typography>
          </Link>
        </SidebarLinkWrapper>
        {order.parts.map((part: OrderPart) => {
          const sidebarLinks = [
            {
              name: navLinks.PRODUCTS,
              route: `/order/${orderId}/products/${part.id}`,
              label: t('orderInfo.products'),
            },
            {
              name: navLinks.FULFILMENT,
              route: `/order/${orderId}/fulfilment/${part.id}`,
              label: t('fulfilment'),
            },
            {
              name: navLinks.EVENTS,
              route: `/order/${orderId}/events/${part.id}`,
              label: t('orderInfo.events'),
            },
          ]
          const dropdownOption = getDropdownOption(part.status.toLowerCase())
          return (
            <div key={part.id}>
              <Typography
                variant='body2'
                mb={theme.spacing(3)}
                mt={theme.spacing(3)}
                sx={{ color: theme.palette.text.secondary }}>
                {t(`orderInfo.partId`, { partId: part.id })}
              </Typography>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'>
                <Box
                  key={part.id}
                  sx={{
                    maxWidth: '223px',
                    minHeight: '32px',
                    borderRadius: '2px',
                    margin: `${theme.spacing(1)} 0px`,
                  }}>
                  <Typography
                    variant='body2'
                    sx={{
                      backgroundColor: theme.palette.icon.main,
                      color: theme.palette.commonColors.white,
                      padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
                      borderRadius: '3px',
                    }}>
                    {t(`orderStatus.${part.status}`).toUpperCase()}
                  </Typography>
                </Box>
                {(part.status === 'collected' ||
                  part.status === 'delivering') && (
                  <IconDropdown
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    minWidth='200px'
                    Icon={MoreHorizIcon}
                    isIconOnly
                    options={dropdownOption}
                    ariaLabel={t('orderProduct.changeStatus')}
                    onChange={(_, value) => {
                      handleChangeOrderStatus(part, value)
                    }}
                  />
                )}
              </Box>
              {sidebarLinks.map((link) => (
                <SidebarLinkWrapper
                  key={link.name}
                  highlighted={
                    location.pathname.split('/')[3] === link.name &&
                    location.pathname.split('/')[4] === part.id
                  }
                  display='flex'
                  alignItems='center'>
                  <Link
                    onClick={() => isMobile && setShowOverlay(true)}
                    style={{
                      textDecoration: 'none',
                      color:
                        location.pathname.split('/')[3] === link.name &&
                        location.pathname.split('/')[4] === part.id
                          ? theme.palette.primary.main
                          : theme.palette.commonColors.black,
                      width: '223px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    to={link.route}>
                    <Typography variant='body2' ml={1}>
                      {link.label}
                    </Typography>
                  </Link>
                </SidebarLinkWrapper>
              ))}
            </div>
          )
        })}
      </SecondarySidebar>
      <OrderCancellationModal
        order={order}
        modal={modal}
        handleCloseModal={handleCloseModal}
      />
    </>
  )
}
